import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import PageLayout from "examples/LayoutContainers/PageLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  Chip,
  Box,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { playerDetailsAPI } from "services/api";

function PlayerDetails() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const seriesId = searchParams.get("s");

  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [seriesName, setSeriesName] = useState("");
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Edit Modal States
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Logs Modal States
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedPlayerName, setSelectedPlayerName] = useState("");
  
  // Alert States
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });

  useEffect(() => {
    if (seriesId) {
      loadPlayerDetails();
    } else {
      setAlert({ show: true, type: "error", message: "Series ID is required" });
      setLoading(false);
    }
  }, [seriesId]);

  useEffect(() => {
    // Filter players based on search term
    if (searchTerm.trim() === "") {
      setFilteredPlayers(players);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = players.filter(
        (player) =>
          player.full_name?.toLowerCase().includes(term) ||
          player.mobile_no?.includes(term) ||
          player.district_name?.toLowerCase().includes(term) ||
          player.role?.toLowerCase().includes(term)
      );
      setFilteredPlayers(filtered);
    }
  }, [searchTerm, players]);

  const loadPlayerDetails = async () => {
    try {
      setLoading(true);
      const response = await playerDetailsAPI.getPlayerDetails(seriesId);
      
      if (response.data.status === 1) {
        setPlayers(response.data.players || []);
        setFilteredPlayers(response.data.players || []);
        setSeriesName(response.data.series_name || "");
        setTotalPlayers(response.data.total_players || 0);
      } else {
        setAlert({ show: true, type: "error", message: response.data.message || "Failed to load player details" });
      }
    } catch (error) {
      console.error("Error loading player details:", error);
      setAlert({ show: true, type: "error", message: "Failed to load player details" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (player) => {
    setEditingPlayer(player);
    setEditFormData({
      full_name: player.full_name || "",
      role: player.role || "",
      keeping_flag: player.keeping_flag || "",
      district_name: player.district_name || "",
      previous_team_details: player.previous_team_details || "",
      cric_batting_style: player.cric_batting_style || "",
      cric_bowling_style: player.cric_bowling_style || "",
      cric_matches: player.cric_matches || "",
      cric_runs: player.cric_runs || "",
      cric_wickets: player.cric_wickets || "",
      jersey_no: player.jersey_no || "",
      jersey_name: player.jersey_name || "",
      jersey_size: player.jersey_size || "",
    });
    setEditModalOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      
      const response = await playerDetailsAPI.updatePlayerDetails({
        series_id: seriesId,
        user_id: editingPlayer.user_id,
        updates: editFormData,
      });

      if (response.data.status === 1) {
        setAlert({ 
          show: true, 
          type: "success", 
          message: `Successfully updated ${response.data.total_changes} field(s)` 
        });
        setEditModalOpen(false);
        loadPlayerDetails(); // Reload data
      } else {
        setAlert({ show: true, type: "error", message: response.data.message || "Failed to update player" });
      }
    } catch (error) {
      console.error("Error updating player:", error);
      setAlert({ show: true, type: "error", message: "Failed to update player details" });
    } finally {
      setSaving(false);
    }
  };

  const handleViewLogs = async (player) => {
    try {
      setLogsLoading(true);
      setSelectedPlayerName(player.full_name);
      setLogsModalOpen(true);
      
      const response = await playerDetailsAPI.getUpdateLogs({
        series_id: seriesId,
        user_id: player.user_id,
      });

      if (response.data.status === 1) {
        setLogs(response.data.logs || []);
      } else {
        setAlert({ show: true, type: "error", message: response.data.message || "Failed to load logs" });
        setLogs([]);
      }
    } catch (error) {
      console.error("Error loading logs:", error);
      setAlert({ show: true, type: "error", message: "Failed to load update logs" });
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <PageLayout>
        <DashboardNavbar />
        <SoftBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </SoftBox>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <DashboardNavbar />
      <SoftBox py={3} px={3}>
        <Card sx={{ width: "100%" }}>
          <SoftBox p={3}>
              {/* Header */}
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SoftBox display="flex" alignItems="center" gap={2}>
                  <IconButton onClick={handleBack} size="small">
                    <ArrowBackIcon />
                  </IconButton>
                  <SoftBox>
                    <SoftTypography variant="h4" fontWeight="bold" color="dark">
                      Player Details Management
                    </SoftTypography>
                    <SoftTypography variant="body2" color="text">
                      {seriesName} • {totalPlayers} Players
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              </SoftBox>

              {/* Alert */}
              {alert.show && (
                <Alert 
                  severity={alert.type} 
                  onClose={() => setAlert({ ...alert, show: false })}
                  sx={{ mb: 2 }}
                >
                  {alert.message}
                </Alert>
              )}

              {/* Search Bar */}
              <SoftBox mb={3} display="flex" alignItems="center" gap={2}>
                <TextField
                  fullWidth
                  placeholder="Search by name, mobile, district, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
                    endAdornment: searchTerm && (
                      <IconButton size="small" onClick={handleClearSearch}>
                        <ClearIcon />
                      </IconButton>
                    ),
                  }}
                  variant="outlined"
                  size="small"
                />
                <Chip 
                  label={`${filteredPlayers.length} ${filteredPlayers.length === 1 ? 'player' : 'players'}`} 
                  color="primary" 
                  variant="outlined" 
                />
              </SoftBox>

              {/* Players Table */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ backgroundColor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: "bold", width: "50px" }}>Sl</TableCell>
                      <TableCell sx={{ fontWeight: "bold", minWidth: "150px" }}>Player</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "110px" }}>Mobile</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "100px" }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "80px" }}>Keeping</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "100px" }}>District</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "120px" }}>Current Club</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "120px" }}>Batting</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "120px" }}>Bowling</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center", width: "70px" }}>Matches</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center", width: "70px" }}>Runs</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center", width: "70px" }}>Wickets</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center", width: "80px" }}>Jersey No</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "100px" }}>Jersey Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center", width: "80px" }}>Jersey Size</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center", width: "100px" }}>Actions</TableCell>
                    </TableRow>
                    {filteredPlayers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={16} align="center" sx={{ py: 4 }}>
                          <SoftTypography variant="body2" color="text">
                            {searchTerm ? "No players found matching your search" : "No players in this series"}
                          </SoftTypography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPlayers.map((player) => (
                        <TableRow key={player.user_id} hover>
                          <TableCell>{player.sl_no || "-"}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar 
                                src={player.profile_pic} 
                                alt={player.full_name}
                                sx={{ width: 32, height: 32 }}
                              />
                              <SoftTypography variant="body2" fontWeight="medium">
                                {player.full_name}
                              </SoftTypography>
                            </Box>
                          </TableCell>
                          <TableCell>{player.mobile_no || "-"}</TableCell>
                          <TableCell>{player.role || "-"}</TableCell>
                          <TableCell>{player.keeping_flag || "-"}</TableCell>
                          <TableCell>{player.district_name || "-"}</TableCell>
                          <TableCell>{player.previous_team_details || "-"}</TableCell>
                          <TableCell>{player.cric_batting_style || "-"}</TableCell>
                          <TableCell>{player.cric_bowling_style || "-"}</TableCell>
                          <TableCell align="center">{player.cric_matches || "-"}</TableCell>
                          <TableCell align="center">{player.cric_runs || "-"}</TableCell>
                          <TableCell align="center">{player.cric_wickets || "-"}</TableCell>
                          <TableCell align="center">{player.jersey_no || "-"}</TableCell>
                          <TableCell>{player.jersey_name || "-"}</TableCell>
                          <TableCell align="center">{player.jersey_size || "-"}</TableCell>
                          <TableCell align="center">
                            <Box display="flex" gap={1} justifyContent="center">
                              <Tooltip title="Edit Details">
                                <IconButton 
                                  size="small" 
                                  color="info"
                                  onClick={() => handleEditClick(player)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Update History">
                                <IconButton 
                                  size="small" 
                                  color="secondary"
                                  onClick={() => handleViewLogs(player)}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </SoftBox>
          </Card>
      </SoftBox>

      {/* Edit Player Modal */}
      <Dialog 
        open={editModalOpen} 
        onClose={() => !saving && setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <SoftTypography variant="h5" fontWeight="bold">
            Edit Player Details
          </SoftTypography>
          {editingPlayer && (
            <SoftTypography variant="body2" color="text">
              {editingPlayer.full_name} • {editingPlayer.mobile_no}
            </SoftTypography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <SoftBox display="flex" flexDirection="column" gap={2}>
            {/* Basic Info */}
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                Full Name
              </SoftTypography>
              <TextField
                fullWidth
                value={editFormData.full_name || ""}
                onChange={(e) => handleEditFormChange("full_name", e.target.value)}
                variant="outlined"
                size="small"
              />
            </SoftBox>

            {/* Role & Keeping */}
            <SoftBox display="flex" gap={2}>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Role
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.role || ""}
                  onChange={(e) => handleEditFormChange("role", e.target.value)}
                  placeholder="e.g., All-rounder, Batsman, Bowler"
                  variant="outlined"
                  size="small"
                />
              </SoftBox>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Keeping
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.keeping_flag || ""}
                  onChange={(e) => handleEditFormChange("keeping_flag", e.target.value)}
                  placeholder="e.g., Yes, No, WK"
                  variant="outlined"
                  size="small"
                />
              </SoftBox>
            </SoftBox>

            {/* District & Current Club */}
            <SoftBox display="flex" gap={2}>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  District
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.district_name || ""}
                  onChange={(e) => handleEditFormChange("district_name", e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </SoftBox>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Current Club
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.previous_team_details || ""}
                  onChange={(e) => handleEditFormChange("previous_team_details", e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </SoftBox>
            </SoftBox>

            {/* Batting & Bowling Style */}
            <SoftBox display="flex" gap={2}>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Batting Style
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.cric_batting_style || ""}
                  onChange={(e) => handleEditFormChange("cric_batting_style", e.target.value)}
                  placeholder="e.g., Right-hand bat"
                  variant="outlined"
                  size="small"
                />
              </SoftBox>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Bowling Style
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.cric_bowling_style || ""}
                  onChange={(e) => handleEditFormChange("cric_bowling_style", e.target.value)}
                  placeholder="e.g., Right-arm fast"
                  variant="outlined"
                  size="small"
                />
              </SoftBox>
            </SoftBox>

            {/* Cricket Stats */}
            <SoftBox display="flex" gap={2}>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Matches
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.cric_matches || ""}
                  onChange={(e) => handleEditFormChange("cric_matches", e.target.value)}
                  variant="outlined"
                  size="small"
                  type="text"
                />
              </SoftBox>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Runs
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.cric_runs || ""}
                  onChange={(e) => handleEditFormChange("cric_runs", e.target.value)}
                  variant="outlined"
                  size="small"
                  type="text"
                />
              </SoftBox>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Wickets
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.cric_wickets || ""}
                  onChange={(e) => handleEditFormChange("cric_wickets", e.target.value)}
                  variant="outlined"
                  size="small"
                  type="text"
                />
              </SoftBox>
            </SoftBox>

            {/* Jersey Details */}
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                Jersey Information
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" gap={2}>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Jersey Number
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.jersey_no || ""}
                  onChange={(e) => handleEditFormChange("jersey_no", e.target.value)}
                  placeholder="e.g., 7, 10, 99"
                  variant="outlined"
                  size="small"
                />
              </SoftBox>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Jersey Name
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.jersey_name || ""}
                  onChange={(e) => handleEditFormChange("jersey_name", e.target.value)}
                  placeholder="e.g., JOHN, JOHN DOE"
                  variant="outlined"
                  size="small"
                />
              </SoftBox>
              <SoftBox flex={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1}>
                  Jersey Size
                </SoftTypography>
                <TextField
                  fullWidth
                  value={editFormData.jersey_size || ""}
                  onChange={(e) => handleEditFormChange("jersey_size", e.target.value)}
                  placeholder="e.g., S, M, L, XL"
                  variant="outlined"
                  size="small"
                />
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <SoftButton 
            variant="outlined" 
            color="secondary" 
            onClick={() => setEditModalOpen(false)}
            disabled={saving}
          >
            Cancel
          </SoftButton>
          <SoftButton 
            variant="gradient" 
            color="info" 
            onClick={handleSaveChanges}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* Update Logs Modal */}
      <Dialog 
        open={logsModalOpen} 
        onClose={() => setLogsModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <SoftTypography variant="h5" fontWeight="bold">
            Update History
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            {selectedPlayerName}
          </SoftTypography>
        </DialogTitle>
        <DialogContent dividers>
          {logsLoading ? (
            <SoftBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </SoftBox>
          ) : logs.length === 0 ? (
            <SoftBox py={4} textAlign="center">
              <SoftTypography variant="body2" color="text">
                No update history found for this player
              </SoftTypography>
            </SoftBox>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>Date/Time</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Old Value</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>New Value</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Updated By</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>IP Address</TableCell>
                  </TableRow>
                  {logs.map((log) => (
                    <TableRow key={log.log_id} hover>
                      <TableCell>
                        <SoftTypography variant="caption">
                          {new Date(log.update_timestamp).toLocaleString()}
                        </SoftTypography>
                      </TableCell>
                      <TableCell>
                        <Chip label={log.field_name} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <SoftTypography variant="caption" color="text">
                          {log.old_value || "-"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell>
                        <SoftTypography variant="caption" fontWeight="medium">
                          {log.new_value || "-"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell>
                        <SoftTypography variant="caption">
                          {log.updated_by_name || "Unknown"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell>
                        <SoftTypography variant="caption" color="text">
                          {log.ip_address || "-"}
                        </SoftTypography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <SoftButton variant="outlined" color="secondary" onClick={() => setLogsModalOpen(false)}>
            Close
          </SoftButton>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

export default PlayerDetails;

